import React, { Fragment, useCallback, useEffect, useRef } from "react"
import MediaQuery from "react-responsive"
import PropTypes from "prop-types"
import classNames from "classnames"
import getValue from "object-getvalue"

// Utils
import { getKeyCode } from "lib/utils/KeyCodes"

// Configs
import BreakPointConfig from "components/Stacker/constants/breakpoints"
import KC from "components/Stacker/config/KeyConfig"

// Actions
import { toggleToggle } from "components/Stacker/actions/ToggleActions"

//	Constants
import AC from "components/Stacker/constants/AlertConstants"

// Components
import DropDownItems from "components/Stacker/components/DropDownItems/DropDownItems"
import Icon from "components/Stacker/components/Icon/Icon"
import Alert from "components/Stacker/components/Alert/Alert"

import "./DropDown.scss"
import shadowDOM from "components/Stacker/utils/shadowDOM"
import { useDispatch, useSelector } from "react-redux"
import { useI18nContext } from "i18n/i18n-react"

function DropDown(props) {
  const dispatch = useDispatch()
  const { LL } = useI18nContext()
  const node = useRef()
  const dropDownSelectButton = useRef()
  const dropDownStatus = useSelector((state) => state.toggle[props.id])

  const {
    className,
    selectedItem = LL.generic.selectDropDownLabel(),
    selectedIcon,

    disableFocus,
    id,
    isShowError,
    isNotFormComponent = false,
    hasFloatingLabel = false,
    label,
    meta,
    labelKey = "label",
    valueKey = "value",
    disabled = false,
  } = props

  let { items = [] } = props

  items = items.filter((item) => {
    return item[labelKey] && item[labelKey].toString().length > 0
  })

  const currentInputValue = isNotFormComponent
    ? selectedItem
    : getValue(props, `input.value.${labelKey}`)

  const handleToggle = useCallback(() => {
    if (dropDownSelectButton.current) {
      dropDownSelectButton.current.focus()
    }
    dispatch(toggleToggle(props.id))
  }, [])

  const handleOutsideClick = useCallback(
    (e) => {
      const currentNode = node.current
      if (currentNode) {
        // ignore clicks on the other dropdown components
        if (!currentNode.contains(e.target)) {
          return
        }
        handleToggle()
      }
    },
    [handleToggle]
  )

  useEffect(() => {
    if (!props.dropDownStatus) {
      shadowDOM.addEventListener("click", handleOutsideClick, false)
    }
    return () => {
      if (!dropDownStatus) {
        shadowDOM.removeEventListener("click", handleOutsideClick, false)
      }
    }
  }, [])

  const handleKeyDown = useCallback(
    (e) => {
      const type = getKeyCode(e.key, e.shiftKey)
      switch (type) {
        case KC.ARROWDOWN:
          e.preventDefault()
          handleToggle()
          break
        default:
          break
      }
    },
    [handleToggle]
  )

  const handleBlur = useCallback(() => {
    props.input.onBlur(props.input.value)
  }, [props.input])

  const changeHandler = useCallback(
    (selectedObj, index) => {
      const { isNotFormComponent, itemAction } = props
      if (isNotFormComponent) {
        itemAction(selectedObj, index)
      } else {
        // https://redux-form.com/6.0.0-alpha.4/docs/faq/customcomponent.md/
        // https://redux-form.com/7.3.0/docs/faq/customcomponent.md/
        const {
          input: { onChange },
        } = props
        onChange(selectedObj)
      }
    },
    [props]
  )

  const nativeSelect = useCallback(
    (e) => {
      const { selectedItem } = props
      const {
        target: { selectedIndex },
      } = e
      const indexDiff =
        selectedItem !== LL.generic.selectDropDownLabel() ? 0 : 1
      // Checking the selected option here
      const { items } = props
      const selectedObj = items[selectedIndex - indexDiff]
      changeHandler(selectedObj, selectedIndex - indexDiff)
    },
    [LL.generic, changeHandler, props]
  )

  // label; // Fix for Floating Label

  return (
    <div className={classNames("DropDown", className)} ref={node}>
      <span
        className={classNames("select-dropdown", {
          error: isShowError,
        })}
      >
        <select
          aria-label={
            label
              ? `${label} ${currentInputValue || label}`
              : `${currentInputValue || label}`
          }
          onChange={nativeSelect}
          value={currentInputValue || ""}
          {...(disableFocus && {
            tabIndex: "-1",
          })}
          className={classNames("select-box d-flex align-items-center", {
            "has-value": hasFloatingLabel && currentInputValue,
            "field-error":
              !isNotFormComponent && meta && meta.touched && meta.error,
          })}
          disabled={disabled}
        >
          <Fragment>
            {selectedItem === LL.generic.selectDropDownLabel() && (
              <option value="">{LL.generic.selectDropDownLabel()}</option>
            )}
            {items &&
              items.map((item, index) => {
                const { size } = item
                const optLabel = item[labelKey]
                return (
                  <option
                    value={size}
                    key={`${size}of${optLabel}`}
                    aria-label={`${optLabel} ${index + 1} of ${items.length}`}
                  >
                    {optLabel}
                  </option>
                )
              })}
          </Fragment>
        </select>
        {!disabled && (
          <Icon
            additionalClassNames="icon-dropdown"
            iconName={
              dropDownStatus
                ? "icon-arrow_dropdown_collapse"
                : "icon-arrow_dropdown_expand"
            }
          />
        )}
      </span>

      {meta && meta.touched && meta.error && (
        <Alert type={AC.ERROR} message={meta.error} />
      )}
    </div>
  )
}

DropDown.propTypes = {
  selectedItem: PropTypes.string,
  id: PropTypes.string,
  disableFocus: PropTypes.bool,
  items: PropTypes.array,
  itemAction: PropTypes.func,
  selectedIcon: PropTypes.string,
  isShowError: PropTypes.bool,
  isNotFormComponent: PropTypes.bool,
  input: PropTypes.object,
  className: PropTypes.string,
  hasFloatingLabel: PropTypes.bool,
  meta: PropTypes.object,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  disabled: PropTypes.bool,
  dropDownStatus: PropTypes.string,
}

//	export default DropDown;
export default DropDown
